import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import eventsData from '../../data/eventsData';

function EventPage() {
  const { eventId } = useParams();
  const event = eventsData.find(event => event.id === eventId);

  // Scroll to top when component mounts and when event changes
  useEffect(() => {
    // Using setTimeout to ensure this runs after the DOM is fully loaded
    const timer = setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto' // Ensures instant scroll without smooth animation
      });
      // Force a reflow to ensure the scroll happens
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 0);
    
    return () => clearTimeout(timer);
  }, [eventId]);

  if (!event) {
    return (
      <div className="container py-5 text-center text-white">
        <h2>Event not found</h2>
        <p>The requested event could not be found.</p>
      </div>
    );
  }

  return (
    <div className="event-page py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="event-header text-center mb-3">
              <div className="event-image mt-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <img 
                  src={`${process.env.PUBLIC_URL}${event.image}`} 
                  alt={event.title} 
                  className="img-fluid rounded-3"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `${process.env.PUBLIC_URL}/assets/placeholder-event.jpg`;
                  }}
                  style={{ maxHeight: '250px', width: '100%', objectFit: 'contain' }}
                />
              </div>
              
              <h1 className="display-4 text-white mb-1">{event.title}</h1>
              <h2 className="h1 text-warning mb-2">({event.subtitle})</h2>
              <div className="event-meta mb-4">
                <span className="badge bg-primary me-2">{event.category}</span>
                <span className="text-white-50 me-3">
                  <i className="bi bi-calendar-event me-1"></i> {event.date}
                </span>
                <span className="text-white-50">
                  <i className="bi bi-clock me-1"></i> {event.time}
                </span>
              </div>
            </div>

            <div className="event-content bg-dark p-4 rounded-3 mb-5">
              <div className="row">
                <div className="col-lg-8">
                  <div className="event-description mb-4">
                    <h3 className="text-warning mb-3">About the Event</h3>
                    <p className="text-white">{event.description}</p>
                  </div>

                  <div className="event-details mb-4">
                    <h3 className="text-warning mb-3">Event Details</h3>
                    <ul className="list-unstyled text-white">
                      <li className="mb-2">
                        <i className="bi bi-people-fill text-warning me-2"></i>
                        <strong>Team Size:</strong> {event.teamSize}
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-cash-coin text-warning me-2"></i>
                        <strong>Registration Fee:</strong> {event.fee}
                      </li>
                      {event.prizes && (
                        <>
                          <li className="mb-2">
                            <i className="bi bi-trophy-fill text-warning me-2"></i>
                            <strong>Winner:</strong> {event.prizes.winner}
                          </li>
                          {event.prizes.runnerUp && (
                            <li className="mb-2">
                              <i className="bi bi-award-fill text-secondary me-2"></i>
                              <strong>Runner Up:</strong> {event.prizes.runnerUp}
                            </li>
                          )}
                        </>
                      )}
                    </ul>
                  </div>

                  {event.rules && (
                    <div className="event-rules mb-4">
                      <h3 className="text-warning mb-3">Rules & Guidelines</h3>
                      <ul className="text-white">
                        {event.rules.map((rule, index) => (
                          <li key={index} className="mb-2">{rule}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="event-cta mb-4 mb-lg-0">
                    {event.status === 'Open' ? (
                      <a 
                        href={event.registrationLink} 
                        className="btn btn-warning btn-lg me-3 mb-4 mt-4 register-now-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Register Now
                      </a>
                    ) : (
                      <div className="alert alert-warning mt-4" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Registration Closed
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="event-sidebar bg-dark p-4 rounded-3 h-100">
                    {event.coordinators && event.coordinators.length > 0 && (
                      <div className="event-coordinators">
                        <h4 className="text-warning mb-3">
                          <i className="bi bi-person-lines-fill me-2"></i>
                          Event Coordinators
                        </h4>
                        {event.coordinators.map((coordinator, index) => (
                          <div key={index} className="mb-3">
                            <p className="text-white mb-1">
                              <strong>{coordinator.name}</strong>
                            </p>
                            <p className="mb-0">
                              <a 
                                href={`tel:${coordinator.phone}`} 
                                className="text-decoration-none text-white-50"
                              >
                                <i className="bi bi-telephone-fill me-2"></i>
                                {coordinator.phone}
                              </a>
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventPage;
